import { humanizeNumber } from './common';

/**
 * Formats a price or price range for display.
 * @param {Object} priceData - The price data object
 * @param {boolean} priceData.price_is_range - Whether the price is a range
 * @param {number} priceData.price - The single price value
 * @param {number} priceData.price_range_start - The start of the price range
 * @param {number} priceData.price_range_end - The end of the price range
 * @param {boolean} isSentenceCase - Whether to capitalize the first letter
 * @returns {string} The formatted price string
 */
export function formatPrice( priceData, isSentenceCase = false ) {
  if ( !priceData ) {
    return null;
  }

  let processedPrice = null;
  const {
    price,
    priceIsRange: isRange,
    priceRangeStart: start,
    priceRangeEnd: end,
  } = priceData;

  if ( isRange ) {
    const humanStart = start ? humanizeNumber( start ) : null;
    const humanEnd = end ? humanizeNumber( end ) : null;

    if ( humanStart && humanEnd ) {
      processedPrice = `From $${humanStart} to $${humanEnd}`;
    } else if ( humanStart && !humanEnd ) {
      processedPrice = `Starting at $${humanStart}`;
    } else if ( !humanStart && humanEnd ) {
      processedPrice = `Up to $${humanEnd}`;
    }
  } else if ( price ) {
    const humanPrice = humanizeNumber( price );
    processedPrice = `$${humanPrice}`;
  }

  if ( !isSentenceCase && processedPrice ) {
    // Ensure first letter is not capitalized
    processedPrice = processedPrice.charAt( 0 ).toLowerCase() + processedPrice.slice( 1 );
  }

  return processedPrice;
}

/**
 * Formats a second person fee amount without dollar sign
 * @param {Object} priceData - The price data object
 * @returns {string} The formatted price string without $ sign
 */
export function formatSecondPersonAmount( priceData ) {
  if ( !priceData ) {
    return null;
  }

  const { price } = priceData;

  if ( price ) {
    const humanPrice = humanizeNumber( price );
    return humanPrice;
  }

  return null;
}

/**
 * Formats a second person fee string
 * @param {Object} secondFeeData - The second person fee data
 * @param {boolean} isMonthly - Whether this is a monthly fee
 * @returns {string} Formatted second person fee string
 */
export function formatSecondPersonFee( secondFeeData, isMonthly = false ) {
  if ( !secondFeeData ) return null;

  const formattedPrice = formatPrice( secondFeeData );
  if ( !formattedPrice ) return null;

  return isMonthly
    ? `Second Person Monthly Fee: ${formattedPrice}`
    : `Second Person Fee: ${formattedPrice}`;
}

/**
 * Builds a fee display object with label and data
 * @param {string} label - Fee label (e.g., "Entrance Fee")
 * @param {Object} feeData - Fee data object
 * @param {Object} secondFeeData - Secondary person fee data
 * @param {boolean} hasSecondPerson - Whether second person fee applies
 * @param {boolean} isMonthly - Whether this is a monthly fee
 * @returns {Object} Fee display object
 */
export function buildFeeDisplay( label, feeData, secondFeeData, hasSecondPerson, isMonthly = false ) {
  if ( !feeData ) return null;

  const display = {
    label,
    data: formatPrice( feeData, true ),
    note: null,
  };

  // Add second person fee if applicable
  if ( hasSecondPerson && secondFeeData ) {
    display.note = formatSecondPersonFee( secondFeeData, isMonthly );
  }

  return display;
}

/**
 * Organizes fee data by financial plan
 * @param {Array} feeGroups - Array of fee group paragraphs
 * @returns {Object} Object with financial plans as keys and fee data as values
 */
export function organizeFeesByFinancialPlan( feeGroups = [] ) {
  const planMap = {};

  // Process fee groups
  feeGroups.forEach( ( group ) => {
    const planId = group?.fieldFinancialPlans?.id || 'default';
    const planName = group?.fieldFinancialPlans?.name || null;

    if ( !planMap[planId] ) {
      planMap[planId] = {
        planId,
        planName,
        entranceFee: group.fieldFee,
        entranceSecondFee: group.fieldSecondFee,
        entranceSecondPerson: group.fieldSecondPerson || false,
        monthlyFee: group.fieldMonthlyFee,
        monthlySecondFee: group.fieldSecondMonthlyFee,
        monthlySecondPerson: group.fieldSecondPerson || false,
      };
    }

    // Add formatted versions of the prices
    if ( planMap[planId].entranceFee ) {
      planMap[planId].entranceFee.formatted = formatPrice( planMap[planId].entranceFee, true );
    }

    if ( planMap[planId].entranceSecondFee ) {
      planMap[planId].entranceSecondFee.formatted = formatPrice( planMap[planId].entranceSecondFee, true );
      planMap[planId].entranceSecondFee.formattedAmount = formatSecondPersonAmount( planMap[planId].entranceSecondFee );
    }

    if ( planMap[planId].monthlyFee ) {
      planMap[planId].monthlyFee.formatted = formatPrice( planMap[planId].monthlyFee, true );
    }

    if ( planMap[planId].monthlySecondFee ) {
      planMap[planId].monthlySecondFee.formatted = formatPrice( planMap[planId].monthlySecondFee, true );
      planMap[planId].monthlySecondFee.formattedAmount = formatSecondPersonAmount( planMap[planId].monthlySecondFee );
    }
  } );

  return planMap;
}

/**
 * Creates fee display objects for individual financial plans
 * @param {Object} planData - Fee data for a specific financial plan
 * @returns {Array} Array of fee display objects
 */
export function createPlanFeeDisplays( planData ) {
  if ( !planData ) return [];

  const displays = [];
  const {
    planName,
    entranceFee,
    entranceSecondFee,
    entranceSecondPerson,
    monthlyFee,
    monthlySecondFee,
    monthlySecondPerson,
  } = planData;

  // Build entrance fee display
  const entranceFeeDisplay = buildFeeDisplay(
    'Entrance Fee',
    entranceFee,
    entranceSecondFee,
    entranceSecondPerson,
    false,
  );

  if ( entranceFeeDisplay ) {
    if ( planName ) {
      entranceFeeDisplay.planName = planName;
    }
    displays.push( entranceFeeDisplay );
  }

  // Build monthly fee display
  const monthlyFeeDisplay = buildFeeDisplay(
    'Monthly Fee',
    monthlyFee,
    monthlySecondFee,
    monthlySecondPerson,
    true,
  );

  if ( monthlyFeeDisplay ) {
    if ( planName ) {
      monthlyFeeDisplay.planName = planName;
    }
    displays.push( monthlyFeeDisplay );
  }

  return displays;
}

/**
 * Processes fee groups from residence data to create displayable fee information.
 * @param {Array} feeGroups - Array of fee group paragraphs
 * @returns {Object} Object containing fee display data
 */
export function processFeeGroups( feeGroups ) {
  // No fee data available
  if ( !feeGroups || feeGroups.length === 0 ) {
    return {
      hasFees: false,
      hasFeePlanNames: false,
      feeDisplays: [],
    };
  }

  // Organize fees by financial plan
  const planMap = organizeFeesByFinancialPlan( feeGroups );
  const planIds = Object.keys( planMap );

  // Generate fee displays for each plan
  const planDisplays = planIds.map( ( planId ) => ( {
    planId,
    planName: planMap[planId]?.planName,
    fees: createPlanFeeDisplays( planMap[planId] ),
  } ) );

  // Collect all fee displays for the standard display
  const feeDisplays = [];
  let hasFeePlanNames = false;
  planDisplays.forEach( ( planDisplay ) => {
    planDisplay.fees.forEach( ( fee ) => {
      const planName = planDisplay?.planName || null;
      if ( !planName ) {
        // If no plan name, use fee type as label
        feeDisplays.push( {
          label: fee.label,
          data: fee.data,
          note: fee.note,
        } );
      } else {
        // If plan name, include plan name in label
        hasFeePlanNames = true; // We have at least one plan with a name
        feeDisplays.push( {
          label: `${planDisplay.planName} - ${fee.label}`,
          data: fee.data,
          note: fee.note,
        } );
      }
    } );
  } );

  return {
    hasFees: feeDisplays.length > 0,
    hasFeePlanNames,
    planMap,
    feeDisplays,
  };
}
